import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card } from '@mui/material';

import { EmptyBox } from '@europrocurement/flexy-components';
import {
    articleIcon,
    codeArticleIcon,
    domainIcon,
    percentIcon,
    priceIcon,
    tagIcon,
    unitIcon,
} from '@europrocurement/l2d-icons';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useOffreService } from '@europrocurement/l2d-domain';
import { ArticlesJsonldArticleRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';

import ArticleForm from './Form';
import { articleFichePath, articleListPath } from './routes';
import { articleCategoryFichePath } from '../ArticleCategory/routes';
import { articleUnitsOptions, exemptedVatOption, vatCodesOptions } from './options';
import FicheHeader, { type FicheSubtitleElement } from '../components/fiche/FicheHeader';
import buildCreationTitle from '../components/fiche/buildCreationTitle';
import LoadingScreen from '../forms/LoadingScreen';

const ARTICLE_SECTION_LABEL = 'Configuration comptabilité';

/** Résout le libellé d'une option de select à partir de sa valeur. */
const findOptionLabel = (
    options: { value: string | number; label: string }[],
    value?: string | number | null,
): string | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    return options.find((option) => option.value === value)?.label;
};

/**
 * Fiche d'un article sur route dédiée (remplace l'édition/création en modale).
 * - `admin/configuration-comptabilite/article/new` : création.
 * - `admin/configuration-comptabilite/article/:id` : édition (chargé par son identifiant).
 */
const ArticleFiche: React.FunctionComponent = function () {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id || id === 'new';

    const { articleModel: model } = useOffreService();
    const { request } = useApiRequest<ArticlesJsonldArticleRead>();

    const [entity, setEntity] = useState<ArticlesJsonldArticleRead | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(!isCreate);
    const [isInError, setIsInError] = useState<boolean>(false);
    // Libellé saisi dans le formulaire (mode création), affiché dans l'en-tête.
    const [draftLabel, setDraftLabel] = useState<string>('');

    useEffect(() => {
        let active = true;
        if (!isCreate) {
            setIsLoading(true);
            setIsInError(false);

            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => {
                    if (active) {
                        setEntity(result.data);
                    }
                },
                failureCallback: () => {
                    if (active) {
                        setIsInError(true);
                    }
                },
            }).finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        }

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isCreate]);

    const goToList = useCallback(() => {
        navigate(articleListPath);
    }, [navigate]);

    // Après un enregistrement :
    // - en création : on ouvre la fiche du nouvel objet ;
    // - en édition : on reste sur la fiche et on rafraîchit l'entité
    //   (sans toggler le chargement, pour ne pas démonter le formulaire).
    const handleAfterSubmit = useCallback(
        (createdId?: number) => {
            if (isCreate) {
                if (createdId != null) {
                    navigate(articleFichePath(createdId));
                }
                return;
            }
            if (!id) {
                return;
            }
            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => setEntity(result.data),
            });
        },
        [id, isCreate, model, navigate, request],
    );

    const title = isCreate
        ? buildCreationTitle("Création d'un article", draftLabel)
        : (entity?.libelle ?? 'Article');

    const subtitleElements: FicheSubtitleElement[] =
        isCreate || !entity
            ? []
            : [
                  {
                      icon: codeArticleIcon.props.icon,
                      label: entity.codeArticle ? `Code : ${entity.codeArticle}` : undefined,
                  },
                  {
                      icon: domainIcon.props.icon,
                      label: entity.domaine?.libelle
                          ? `Domaine : ${entity.domaine.libelle}`
                          : undefined,
                  },
                  {
                      icon: tagIcon.props.icon,
                      label: `Catégorie : ${entity.catArticle?.libelle ?? 'Aucune'}`,
                      link: entity.catArticle?.id
                          ? articleCategoryFichePath(entity.catArticle.id)
                          : undefined,
                      linkTooltip: "Ouvrir la catégorie d'article",
                  },
                  {
                      icon: priceIcon.props.icon,
                      label:
                          entity.prixUnitaire !== undefined && entity.prixUnitaire !== null
                              ? `Prix : ${entity.prixUnitaire} €`
                              : undefined,
                  },
                  {
                      icon: unitIcon.props.icon,
                      label: findOptionLabel(articleUnitsOptions, entity.unite)
                          ? `Unité : ${findOptionLabel(articleUnitsOptions, entity.unite)}`
                          : undefined,
                  },
                  {
                      icon: percentIcon.props.icon,
                      label: findOptionLabel(
                          [...vatCodesOptions, ...exemptedVatOption],
                          entity.natureTva,
                      )
                          ? `TVA : ${findOptionLabel(
                                [...vatCodesOptions, ...exemptedVatOption],
                                entity.natureTva,
                            )}`
                          : undefined,
                  },
              ];

    const renderContent = () => {
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isInError || (!isCreate && !entity)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '30vh',
                    }}
                >
                    <EmptyBox
                        text="Article introuvable"
                        subText="Cet article n'existe pas ou n'a pas pu être chargé."
                    />
                </Box>
            );
        }

        return (
            <ArticleForm
                isCreate={isCreate}
                entity={entity}
                afterSubmit={handleAfterSubmit}
                onCancel={goToList}
                onLabelChange={setDraftLabel}
            />
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <FicheHeader
                icon={articleIcon}
                title={title}
                reference={isCreate ? undefined : entity?.id}
                subtitleElements={subtitleElements}
                sectionLabel={ARTICLE_SECTION_LABEL}
                listPath={articleListPath}
            />

            <Card sx={{ marginTop: 2, padding: 3 }}>{renderContent()}</Card>
        </Box>
    );
};

export default ArticleFiche;
